import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import {
  ADD_GITHUB_EMAIL_MAPPING_FILE,
  AddGithubEmailMappingFileMutation,
  AddGithubEmailMappingFileMutationVariables,
  GET_BACKGROUND_OPERATIONS,
  GET_COMPANY,
  GET_GITHUB_EMAIL_MAPPING_DATA,
  GET_GITHUB_EMAIL_MAPPING_SIGNED_UPLOAD_URL,
  GET_ONE_SCHEMA_JWT,
  GetBackgroundOperationsQueryVariables,
  GetCompanyQueryVariables,
  GetGithubEmailMappingDataQueryVariables,
  GetGithubEmailMappingSignedUploadUrlMutation,
  GetGithubEmailMappingSignedUploadUrlMutationVariables,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
} from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';

type UseAddGithubEmailMappingFileParams = {
  companyId: string;
  isOpen: boolean;
};

type UseAddGithubEmailMappingFileReturn = {
  handleSuccess: () => Promise<void>;
  isLoading: boolean;
  oneSchemaJwt: string | null;
  permanentUrl: string | null;
  signedUrl: string | null;
};

const ERROR_TOAST_TITLE = 'Error uploading GitHub email mapping';

export const useAddGithubEmailMappingFile = ({
  companyId,
  isOpen,
}: UseAddGithubEmailMappingFileParams): UseAddGithubEmailMappingFileReturn => {
  const { user } = useCurrentUser();
  const [getOneSchemaJwt, { data: oneSchemaJwtData }] = useMutation<
    GetOneSchemaJwtMutation,
    GetOneSchemaJwtMutationVariables
  >(GET_ONE_SCHEMA_JWT);
  const [getUploadUrlMutation, { data: uploadUrlData, loading: isLoading }] = useMutation<
    GetGithubEmailMappingSignedUploadUrlMutation,
    GetGithubEmailMappingSignedUploadUrlMutationVariables
  >(GET_GITHUB_EMAIL_MAPPING_SIGNED_UPLOAD_URL);
  const [addGithubEmailMappingFile] = useMutation<
    AddGithubEmailMappingFileMutation,
    AddGithubEmailMappingFileMutationVariables
  >(ADD_GITHUB_EMAIL_MAPPING_FILE);

  useEffect(() => {
    const initOneSchemaJwt = async (): Promise<void> => {
      if (!isOpen || !user?.id) {
        return;
      }
      await getOneSchemaJwt({
        variables: { userId: user.id },
        context: {
          errorToast: { title: ERROR_TOAST_TITLE },
        },
      });
    };
    initOneSchemaJwt();
  }, [getOneSchemaJwt, isOpen, user?.id]);

  useEffect(() => {
    const initUploadUrls = async (): Promise<void> => {
      if (!isOpen) {
        return;
      }
      await getUploadUrlMutation({
        variables: { companyId },
        context: {
          errorToast: { title: ERROR_TOAST_TITLE },
        },
      });
    };
    initUploadUrls();
  }, [companyId, getUploadUrlMutation, isOpen]);

  const handleSuccess = useCallback(async (): Promise<void> => {
    const permanentUrl = uploadUrlData?.getGithubEmailMappingSignedUploadUrl.permanentUrl;
    if (!permanentUrl) {
      return;
    }

    await addGithubEmailMappingFile({
      variables: {
        companyId,
        input: { permanentUrl },
      },
      refetchQueries: [
        {
          query: GET_GITHUB_EMAIL_MAPPING_DATA,
          variables: { companyId } satisfies GetGithubEmailMappingDataQueryVariables,
        },
        {
          query: GET_COMPANY,
          variables: { companyId } satisfies GetCompanyQueryVariables,
        },
        {
          query: GET_BACKGROUND_OPERATIONS,
          variables: { companyId } satisfies GetBackgroundOperationsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
      context: {
        successToast: {
          title: 'GitHub email mapping upload started!',
          description:
            "We're matching your file to GitHub contributors. The upload's status updates once processing completes.",
        },
        errorToast: { title: ERROR_TOAST_TITLE },
      },
    });
  }, [addGithubEmailMappingFile, companyId, uploadUrlData]);

  return {
    handleSuccess,
    isLoading,
    oneSchemaJwt: oneSchemaJwtData?.getOneSchemaJwt ?? null,
    permanentUrl: uploadUrlData?.getGithubEmailMappingSignedUploadUrl.permanentUrl ?? null,
    signedUrl: uploadUrlData?.getGithubEmailMappingSignedUploadUrl.signedUrl ?? null,
  };
};
